<template>
    <div class="station-info">
        <div class="address">
            <DSIcon code="default-fill/pin" size="xxl" />
            <h4>{{ t('contact') }}</h4>
            <p>
                <span>{{ station.name }}</span>
                <span>{{ station.street }}</span>
                <span>{{ station.postcode }} <span class="city">{{ station.city }}</span></span>
            </p>
        </div>
        <div class="call">
            <DSIcon code="default-fill/call" size="xxl" />
            <h4>{{ t('phoneNumber') }}</h4>
            <p>
                <a :href="`tel:${station.telephone}`">{{ station.telephone }}</a>
            </p>
        </div>
        <div class="email">
            <DSIcon v-if="station.email" code="default-fill/mail" size="xxl" />
            <p v-if="station.email">
                <span>{{ t('email') }}</span>
                <strong><a :href="`mailto:${station.email}`">{{ station.email }}</a></strong>
            </p>
        </div>
        <template
            v-for="openingHours in ['station', 'kitchen', 'lunch', 'breakfast']"
            :key="`open-hour-${openingHours}`"
        >
            <div
                v-if="station.openingHoursTable?.[openingHours]?.length"
                :class="`open-hours open-hours-${openingHours}`"
            >
                <DSIcon :code="openingHours === 'station' ? 'clock' : 'restaurantstations'" size="xxl" />
                <BlocksOpeningHours :name="t(openingHours)" :opening-hours="station.openingHoursTable[openingHours]" />
            </div>
        </template>
    </div>
</template>
<script setup>
import { useI18n, useSeoMeta } from '#imports';
import { computed } from 'vue';

const { t } = useI18n({
    useScope: 'global',
    messages: {
        'en-EU': {
            opens: 'Opening hours',
            contact: 'Contact',
            stationOgDescription: 'See station information and opening hours. {address}',
        },
        'fi-FI': {
            opens: 'Aukioloajat',
            contact: 'Yhteystiedot',
            stationOgDescription: 'Katso aseman tiedot ja aukioloajat. {address}',
        },
        'sv-SE': {
            opens: 'Öppettider',
            contact: 'Hitta hit',
            stationOgDescription: 'Se stationsinformation och öppettider. {address}',
        },
        'no-NO': {
            opens: 'Åpningstider',
            contact: 'Kontakt',
            stationOgDescription: 'Se stasjonsinformasjon og åpningstider. {address}',
        },
    },
});

const { station } = defineProps({
    station: {
        type: Object,
        required: false,
        default: () => ({}),
    },
});
const stationAddress = computed(() => `${station?.name}, ${station?.street ?? ''} ${station?.postcode ?? ''} ${station?.city ?? ''}`.trim());

useSeoMeta({
    titleTemplate: station?.name ?? null,
    description: t('stationOgDescription', { address: stationAddress.value }),
    ogTitle: station?.name ?? null,
    ogDescription: t('stationOgDescription', { address: stationAddress.value }),
});

</script>
<script>
export default {
    name: 'StationHeaderInfo',
    methods: {},
};

</script>
<style scoped>
.station-info {
  display: grid;
  margin: 2rem 0 0;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 1.5rem;
  border-radius: 4px;
  position: relative;
  z-index: 1;
  background: var(--color-base-white);
}
.station-info .address,
.station-info .call,
.station-info .open-hours,
.station-info .email {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0 1rem;
}
.station-info .address .icon,
.station-info .call .icon,
.station-info .open-hours .icon,
.station-info .email .icon {
  grid-row-start: 1;
  grid-row-end: 3;
}
.station-info .address h4,
.station-info .call h4,
.station-info .open-hours h4,
.station-info .email h4 {
  grid-column-start: 2;
}
.station-info .address p,
.station-info .call p,
.station-info .open-hours p,
.station-info .email p {
  grid-column-start: 2;
}
.station-info .email,
.station-info .call {
  grid-row-start: 2;
}
.station-info .email span,
.station-info .call span {
  display: block;
}
.station-info .email {
  max-width: 100%;
  overflow: hidden;
  word-break: break-word;
}
.station-info .address strong, .station-info .address p > span {
  display: block;
}
@media (max-width: 1199px) {
  .station-info {
    grid-template-columns: repeat(2, 1fr);
    margin: 2rem 1rem;
  }
  .station-info .address p {
    grid-column-start: 2;
  }
  .station-info .call {
    grid-column-start: 1;
    grid-row-start: 2;
  }
  .station-info .call p {
    grid-column-start: 2;
    margin-top: 0;
  }
  .station-info .email {
    grid-column-start: 2;
    grid-row-start: 2;
  }
  .station-info .email p {
    grid-column-start: 2;
    margin-top: 0;
  }
}
@media (max-width: 991px) {
  .station-info {
    grid-template-columns: 1fr;
  }
  .station-info > * {
    grid-column-start: 1;
  }
  .station-info .icon {
    color: var(--color-base-grey-800);
  }
  .station-info > *:not(.link) {
    display: grid;
    grid-template-columns: 2rem 1fr;
  }
  .station-info > *:not(.link) a {
    text-decoration: none;
  }
  .station-info > *:not(.link) p {
    margin-top: 0;
  }
  .station-info > *:not(.link) p strong {
    margin-bottom: 0.25rem;
  }
  .station-info > *:not(.link) p > * {
    display: block;
  }
  .station-info .link a {
    color: var(--color-primary-900);
    font-weight: 700;
  }
  .station-info .link a:hover {
    text-decoration: underline;
  }
  .station-info .email {
    grid-column-start: 1;
    grid-row-start: auto;
  }
  .station-info .email p {
    grid-column-start: 2;
    margin-top: 0;
  }
}
</style>
